import { UnmatchedDemandMatchingStatus, UnmatchedMasterProductDemand } from 'apis/restaurant-inquiries';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Tag,
  Td,
  Text,
  Tooltip,
  Tr,
  VStack,
  Wrap,
  WrapItem,
} from 'design-system';
import { Fragment } from 'react';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';

dayjs.extend(relativeTime);

// Handoff §Waiting: green <2d, amber 2-3d, red >=3d OR (no_quotes AND >=2d).
// Implemented as written — the reference mockup has two rows contradicting this
// rule (plan doc §4), so those specific rows' colors are intentionally not copied.
const urgencyColor = (days: number, status: UnmatchedDemandMatchingStatus) => {
  if (days >= 3 || (status === UnmatchedDemandMatchingStatus.NO_QUOTES && days >= 2)) return 'red.500';
  if (days >= 2) return 'yellow.400';
  return 'green.400';
};

// 'GRAMS' -> 'Grams'
const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

export interface IDemandTableRow {
  row: UnmatchedMasterProductDemand;
  isExpanded: boolean;
  onToggle: () => void;
}

export const DemandTableRow = ({ row, isExpanded, onToggle }: IDemandTableRow) => {
  const meta = [
    row.brand,
    row.origin,
    row.state,
    row.packSize && row.packUnit ? `${row.packSize} ${titleCase(row.packUnit)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const waitingDays = Math.max(0, dayjs().diff(dayjs(row.firstRequestedAt), 'day'));
  const isNoQuotes = row.matchingStatus === UnmatchedDemandMatchingStatus.NO_QUOTES;
  const latestQuote = row.quotes[0]; // BE returns quotes newest-first

  return (
    <Fragment>
      <Tr _hover={{ bg: 'gray.50' }}>
        <Td cursor='pointer' onClick={onToggle}>
          <HStack spacing='xs' align='start'>
            <Icon as={isExpanded ? BiChevronDown : BiChevronRight} color='gray.400' mt='3px' />
            <Box>
              <Text fontWeight='bold' textTransform='capitalize'>
                {row.name ?? '—'}
              </Text>
              {meta && (
                <Text fontSize='xs' color='gray.500' textTransform='capitalize'>
                  {meta}
                </Text>
              )}
            </Box>
          </HStack>
        </Td>

        <Td>
          <HStack spacing='xs'>
            <Text fontWeight='extrabold' fontSize='md' whiteSpace='nowrap'>
              {row.totalPerMonth.toLocaleString()}{' '}
              <Text as='span' fontWeight='medium' fontSize='xs' color='gray.500'>
                {row.baseUnit}/mo
              </Text>
            </Text>
            {row.suspect && (
              <Tooltip label='One or more quantities look like test data' hasArrow>
                <Text as='span' color='orange.400' cursor='help'>
                  ⚠
                </Text>
              </Tooltip>
            )}
          </HStack>
          <Text fontSize='xs' color='gray.500'>
            {row.inquiryCount} {row.inquiryCount === 1 ? 'inquiry' : 'inquiries'} ·{' '}
            <Button variant='link' size='xs' colorScheme='blue' onClick={onToggle}>
              view
            </Button>
          </Text>
        </Td>

        <Td>
          {isNoQuotes ? (
            <Badge colorScheme='red' px='sm' py='2px' borderRadius='full' fontSize='2xs'>
              No quotes yet
            </Badge>
          ) : (
            <Badge colorScheme='orange' px='sm' py='2px' borderRadius='full' fontSize='2xs'>
              {row.quotesReceivedCount} {row.quotesReceivedCount === 1 ? 'quote' : 'quotes'} · no match
            </Badge>
          )}
          <Text fontSize='xs' color='gray.500' mt='4px'>
            {row.quotesReceivedCount > 0 ? (
              <>
                <Text as='span' fontWeight='semibold' color='gray.700'>
                  {row.quotesReceivedCount} {row.quotesReceivedCount === 1 ? 'quote' : 'quotes'} received
                </Text>
                {latestQuote && <> · last quote {dayjs(latestQuote.receivedAt).fromNow()}</>}
              </>
            ) : (
              '0 quotes received yet'
            )}
          </Text>
        </Td>

        <Td>
          <HStack spacing='xs'>
            <Box w='8px' h='8px' borderRadius='full' bg={urgencyColor(waitingDays, row.matchingStatus)} />
            <Text fontWeight='semibold' whiteSpace='nowrap'>
              {waitingDays} {waitingDays === 1 ? 'day' : 'days'}
            </Text>
          </HStack>
          <Text fontSize='xs' color='gray.500'>
            first asked {dayjs(row.firstRequestedAt).format('D MMM')}
          </Text>
        </Td>

        <Td>
          <HStack justify='flex-end' spacing='sm'>
            {row.quotesReceivedCount > 0 && (
              <Button size='xs' variant='outline' onClick={onToggle}>
                View quotes
              </Button>
            )}
          </HStack>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr bg='gray.50'>
          <Td colSpan={5} py='md'>
            <Flex gap='xl' bg='white' border='1px solid' borderColor='gray.200' borderRadius='lg' p='md' wrap='wrap'>
              <Box>
                <Text
                  fontSize='2xs'
                  fontWeight='bold'
                  textTransform='uppercase'
                  letterSpacing='wide'
                  color='gray.500'
                  mb='sm'>
                  Volume breakdown
                </Text>
                <Wrap maxW='400px'>
                  {row.inquiryItems.map((item, index) => (
                    <WrapItem key={index}>
                      <Tag size='sm' borderRadius='md' colorScheme='gray'>
                        <Text as='span' fontWeight='semibold'>
                          {item.qty != null ? item.qty.toLocaleString() : '—'}
                        </Text>
                        &nbsp;{row.baseUnit}/mo · {item.restaurantName ?? `#${item.restaurantId}`}
                        {item.suspect && (
                          <Tooltip label='This quantity looks like test data' hasArrow>
                            <Text as='span' ml='4px' color='orange.400' cursor='help'>
                              ⚠
                            </Text>
                          </Tooltip>
                        )}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              <Box>
                <Text
                  fontSize='2xs'
                  fontWeight='bold'
                  textTransform='uppercase'
                  letterSpacing='wide'
                  color='gray.500'
                  mb='sm'>
                  Quotes ({row.quotesReceivedCount})
                </Text>
                {row.quotes.length ? (
                  <VStack align='stretch' spacing='xs'>
                    {row.quotes.map((quote, index) => (
                      <HStack key={index} spacing='sm' fontSize='sm'>
                        <Text fontWeight='semibold' minW='150px'>
                          {quote.supplierName ?? `Supplier #${quote.supplierId}`}
                        </Text>
                        <Text color='gray.600'>
                          {quote.price.toLocaleString()} {quote.currency}/{row.baseUnit}
                        </Text>
                        <Tag size='sm' borderRadius='full' colorScheme='orange' fontSize='2xs'>
                          received {dayjs(quote.receivedAt).fromNow()}
                        </Tag>
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize='sm' color='gray.500'>
                    No quotes received yet
                  </Text>
                )}
              </Box>
            </Flex>
          </Td>
        </Tr>
      )}
    </Fragment>
  );
};
